import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import {
  doc,
  getDoc,
  setDoc,
  Timestamp,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { httpsCallable } from "firebase/functions";

import { db, functions } from "../firebase";
import { trackProductEvent } from "../lib/analytics";
import { buildUserRoadmapFromFirebase } from "../utils/buildUserRoadmapFromFirebase";

/* ================= CONFIG ================= */
const TECHNOLOGY_TO_ROADMAP_TYPE = {
  "react-native": "Mobile Development",
  flutter: "Mobile Development",
  react: "Frontend Development",
  nextjs: "Frontend Development",
  node: "Backend Development",
};

const TECHNOLOGY_TO_ROLE_KEY = {
  "react-native": "mobile",
  flutter: "mobile",
  react: "frontend",
  nextjs: "frontend",
  node: "backend",
};
const TECHNOLOGY_TO_BASE_ROADMAP = {
  "react-native": "React Native Developer",
  flutter: "Flutter Developer",
  "ios-swift": "iOS Developer",
  react: "Frontend Developer",
  node: "Backend Developer",
};

const TIMELINE_CONFIG = {
  fast: { maxWeeks: 8 },
  comfortable: { maxWeeks: 12 },
  relaxed: { maxWeeks: 20 },
};

/* ================= COMPONENT ================= */
const normalizeStage = (level) => {
  if (!level) return "Beginner";
  if (level === "Absolute Beginner") return "Beginner";
  return level;
};

export default function OnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const path = state?.path || "product";
  const technology = state?.technology || null;
  const clarityResult = state?.clarityResult || {};
  const normalizedProfile = state?.profile?.normalized || {};
  const replaceConfirmed = !!state?.replaceConfirmed;

  /* ================= DERIVED ================= */

  const roadmapType = useMemo(() => {
    if (path === "dsa") return "DSA";
    return TECHNOLOGY_TO_ROADMAP_TYPE[technology] || null;
  }, [path, technology]);

  const roleKey = useMemo(() => {
    if (path === "dsa") return "dsa";
    return TECHNOLOGY_TO_ROLE_KEY[technology] || "frontend";
  }, [path, technology]);

  const [stage, setStage] = useState("");
  const [goal, setGoal] = useState("");
  const [timeline, setTimeline] = useState("comfortable");
  const [loading, setLoading] = useState(false);

  /* ================= ROADMAP META ================= */

  const [roadmapMeta, setRoadmapMeta] = useState(null);
  const [phases, setPhases] = useState([]);
  const [selectedPhases, setSelectedPhases] = useState([]);

  /* ================= MAP CLARITY ================= */

  useEffect(() => {
    if (!clarityResult || !state?.profile) return;

    setStage(normalizeStage(clarityResult.level));
    setGoal(state.profile.normalized.job_intent || "Get job");
  }, [clarityResult, state]);

  /* ================= FETCH ROADMAP META ================= */
  useEffect(() => {
    if (!roadmapType || !technology) return;

    const fetchMeta = async () => {
      const expectedBaseRoadmap = TECHNOLOGY_TO_BASE_ROADMAP[technology];

      const q = query(
        collection(db, "roadmaps_meta"),
        where("roadmapType", "==", roadmapType),
        where("status", "==", "PUBLISHED")
      );

      const snap = await getDocs(q);

      const matched = snap.docs.find(
        (d) => d.data().baseRoadmap === expectedBaseRoadmap
      );

      if (!matched) {
        console.error(
          "❌ No matching roadmap found for technology:",
          technology
        );
        return;
      }

      setRoadmapMeta({
        id: matched.id,
        ...matched.data(),
      });
    };

    fetchMeta();
  }, [roadmapType, technology]);

  /* ================= FETCH PHASES ================= */

  useEffect(() => {
    if (!roadmapMeta?.id) return;

    const fetchPhases = async () => {
      const q = query(
        collection(db, "roadmap_phases"),
        where("roadmapId", "==", roadmapMeta.id)
        // where("isActive", "==", true)
      );

      const snap = await getDocs(q);

      const data = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => a.order - b.order);

      setPhases(data);
      setSelectedPhases(data.map((p) => p.phaseKey));
    };

    fetchPhases();
  }, [roadmapMeta]);

  /* ================= SUBMIT ================= */

  const submit = async () => {
    if (!user) return alert("Login required");
    if (!roadmapMeta) return alert("Roadmap not found");

    try {
      setLoading(true);

      const existingRoadmapSnap = await getDoc(doc(db, "roadmaps", user.uid));
      if (existingRoadmapSnap.exists() && !replaceConfirmed) {
        setLoading(false);
        alert(
          "You already have a roadmap. Please confirm replacement before creating a new one."
        );
        navigate("/my-roadmap");
        return;
      }

      const timelineConfig =
        TIMELINE_CONFIG[timeline] || TIMELINE_CONFIG.comfortable;

      const roadmapPhases = await buildUserRoadmapFromFirebase({
        roadmapMetaId: roadmapMeta.id,
        selectedPhases,
        maxWeeks: timelineConfig.maxWeeks,
      });
      const row = {
        roadmapMetaId: roadmapMeta.id,
        roadmapKey: roadmapMeta.key,
        roadmapVersion: roadmapMeta.version,

        source: "career-clarity",

        claritySnapshot: {
          path,
          technology,
          direction: clarityResult.direction,
          level: clarityResult.level,
          focus: clarityResult.focus,
          reasons: clarityResult.reasons,
        },

        direction: {
          roadmapType,
          roleKey,
          stage,
          goal,
          timeline,
        },

        customization: {
          selectedPhases,
        },

        roadmapRef: {
          roadmapMetaId: roadmapMeta.id,
          phases: roadmapPhases,
        },

        progress: {
          currentPhaseKey: selectedPhases[0],
          currentWeek: 1,
          currentDay: 1,
          completedDays: {},
        },

        createdAt: Timestamp.now(),
      };
      // console.log("row", row);
      await setDoc(doc(db, "roadmaps", user.uid), row);

      void trackProductEvent(
        "roadmap_created",
        {
          roadmapType,
          roleKey,
          stage,
          goal,
          selectedPhasesCount: selectedPhases.length,
        },
        { user }
      );

      await httpsCallable(
        functions,
        "sendRoadmapCreatedEmail"
      )({
        email: user.email,
      });

      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      alert("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */

  if (!roadmapMeta) {
    return (
      <Page>
        <BackgroundOrb $top="-8%" $left="-10%" $color="rgba(250, 176, 5, 0.24)" />
        <BackgroundOrb $top="18%" $right="-12%" $color="rgba(34, 211, 238, 0.18)" />
        <LoadingShell>
          <LoadingEyebrow>Preparing your setup</LoadingEyebrow>
          <LoadingTitle>Building your personalized roadmap workspace</LoadingTitle>
          <LoadingText>
            We are matching the right roadmap structure, phases, and execution flow
            for your goal.
          </LoadingText>
          <LoadingBar>
            <LoadingProgress />
          </LoadingBar>
        </LoadingShell>
      </Page>
    );
  }

  const phaseCount = phases.length;
  const selectedCount = selectedPhases.length;
  const stageText = stage || "Beginner";
  const goalText = goal || "Get job";
  const whyText = `You are at ${normalizedProfile.stage || stageText} level, aiming to ${goalText.toLowerCase()}, and this roadmap keeps the path focused on ${roadmapMeta.title}.`;

  return (
    <Page>
      <BackgroundOrb $top="-8%" $left="-10%" $color="rgba(250, 176, 5, 0.26)" />
      <BackgroundOrb $top="15%" $right="-12%" $color="rgba(34, 211, 238, 0.18)" />
      <BackgroundOrb $bottom="12%" $left="12%" $color="rgba(250, 176, 5, 0.12)" />
      <Center>
        <HeroSection
          as={motion.section}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
        >
          <HeroCopy>
            <Eyebrow>Create your roadmap</Eyebrow>
            <Title>Your execution plan is almost ready</Title>
            <HeroText>
              Review your setup, keep the phases you want, and generate a roadmap
              built around steady daily progress.
            </HeroText>
            <StatGrid>
              {[
                { label: "Selected track", value: roadmapMeta.title },
                { label: "Current stage", value: stageText },
                { label: "Timeline pace", value: timeline },
              ].map((item) => (
                <StatCard key={item.label}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </StatCard>
              ))}
            </StatGrid>
          </HeroCopy>

          <HeroPanel>
            <PanelLabel>Roadmap preview</PanelLabel>
            <PanelTitle>From clarity to daily execution</PanelTitle>
            <PanelText>
              Your roadmap will turn this direction into a clear phase, week, and
              day system so you always know what to do next.
            </PanelText>

            <PanelHighlights>
              <HighlightItem>
                <span>{selectedCount}</span>
                phases selected
              </HighlightItem>
              <HighlightItem>
                <span>{TIMELINE_CONFIG[timeline]?.maxWeeks || 12}</span>
                max learning weeks
              </HighlightItem>
              <HighlightItem>
                <span>{goalText}</span>
                goal direction
              </HighlightItem>
            </PanelHighlights>
          </HeroPanel>
        </HeroSection>

        <Section>
          <SectionHeader>
            <Question>Your setup</Question>
            <SectionLead>Auto-filled from your clarity and path selection.</SectionLead>
          </SectionHeader>
          <InfoGrid>
            {[
              { label: "Stage", value: stageText },
              { label: "Roadmap", value: roadmapMeta.title },
              { label: "Primary goal", value: goalText },
            ].map((i) => (
              <InfoCard key={i.label}>
                <InfoLabel>{i.label}</InfoLabel>
                <InfoValue>{i.value}</InfoValue>
              </InfoCard>
            ))}
          </InfoGrid>
        </Section>

        <WhyCard>
          <InfoLabel>Why this roadmap</InfoLabel>
          <WhyTitle>Built around the direction you already chose</WhyTitle>
          <WhyText>{whyText}</WhyText>
        </WhyCard>

        <Section>
          <SectionHeader>
            <Question>Included learning phases</Question>
            <SectionLead>
              Keep the phases that match your current direction. Only selected phases
              will be used in your roadmap.
            </SectionLead>
          </SectionHeader>

          <Options>
            {phases.map((p, i) => {
              const active = selectedPhases.includes(p.phaseKey);
              return (
                <Option
                  key={p.id}
                  active={active}
                  onClick={() =>
                    setSelectedPhases((prev) =>
                      prev.includes(p.phaseKey)
                        ? prev.filter((k) => k !== p.phaseKey)
                        : [...prev, p.phaseKey]
                    )
                  }
                >
                  <OptionMeta>Phase {i + 1}</OptionMeta>
                  <h4>{p.title}</h4>
                  <p>{p.level || "Core roadmap phase"}</p>
                  {active && <SelectedIcon size={18} />}
                </Option>
              );
            })}
          </Options>
        </Section>

        <CTAWrap>
          <CTA onClick={submit} disabled={loading || !selectedPhases.length}>
            {loading ? "Generating roadmap…" : "Generate My Roadmap →"}
          </CTA>
          <CTAHelper>
            {phaseCount
              ? `${selectedCount} of ${phaseCount} phases selected`
              : "Preparing your roadmap structure"}
          </CTAHelper>
        </CTAWrap>
      </Center>
    </Page>
  );
}

/* ================= STYLES ================= */

const Page = styled.section`
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  padding: 120px 24px 80px;
  background:
    radial-gradient(circle at top left, rgba(250, 176, 5, 0.08), transparent 28%),
    radial-gradient(circle at top right, rgba(34, 211, 238, 0.08), transparent 24%),
    linear-gradient(180deg, #07111f 0%, #091526 54%, #08111c 100%);
`;

const Title = styled.h1`
  font-size: clamp(2.3rem, 4vw, 4rem);
  line-height: 1.02;
  font-weight: 900;
  margin: 0;
  color: #f8fbff;
`;

const Section = styled.div`
  margin-bottom: 40px;
`;

const Question = styled.h3`
  font-size: 1.4rem;
  font-weight: 900;
  margin: 0;
  color: #f6fbff;
`;

const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 18px;
`;

const Option = styled.button`
  position: relative;
  padding: 24px 22px;
  border-radius: 24px;
  cursor: pointer;
  text-align: left;
  overflow: hidden;
  background: ${({ active }) =>
    active
      ? "linear-gradient(180deg, rgba(250, 176, 5, 0.18), rgba(34, 211, 238, 0.12))"
      : "linear-gradient(180deg, rgba(10, 22, 38, 0.94), rgba(8, 17, 30, 0.94))"};
  border: 1px solid
    ${({ active }) => (active ? "rgba(250, 176, 5, 0.55)" : "rgba(140, 166, 197, 0.18)")};
  color: #f6fbff;
  box-shadow: ${({ active }) =>
    active ? "0 18px 48px rgba(250, 176, 5, 0.14)" : "0 18px 42px rgba(0, 0, 0, 0.18)"};
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ active }) =>
      active ? "rgba(250, 176, 5, 0.72)" : "rgba(34, 211, 238, 0.32)"};
  }

  h4 {
    font-size: 1.02rem;
    font-weight: 800;
    margin: 0 0 8px;
    color: #f9fbff;
  }

  p {
    margin: 0;
    font-size: 0.95rem;
    color: rgba(215, 227, 243, 0.74);
  }
`;

const SelectedIcon = styled(CheckCircle)`
  position: absolute;
  top: 14px;
  right: 14px;
  color: #f9b005;
`;

const OptionMeta = styled.div`
  display: inline-flex;
  margin-bottom: 12px;
  padding: 7px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(140, 166, 197, 0.18);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(194, 215, 239, 0.76);
`;

const CTAWrap = styled.div`
  text-align: center;
  margin-top: 56px;
`;

const CTA = styled.button`
  padding: 17px 34px;
  border-radius: 18px;
  background: linear-gradient(135deg, #f3ae1d 0%, #f59e0b 45%, #22d3ee 100%);
  color: #08111d;
  font-size: 1rem;
  font-weight: 900;
  border: none;
  cursor: pointer;
  box-shadow: 0 20px 48px rgba(245, 158, 11, 0.22);
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
    transform: none;
  }
`;

const InfoCard = styled.div`
  padding: 22px;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(10, 22, 38, 0.94), rgba(8, 17, 30, 0.94));
  border: 1px solid rgba(140, 166, 197, 0.18);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
`;

const Center = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1160px;
  margin: 0 auto;
`;

const HeroSection = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(320px, 0.95fr);
  gap: 26px;
  margin-bottom: 36px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCopy = styled.div`
  padding: 32px;
  border-radius: 32px;
  background: linear-gradient(180deg, rgba(10, 22, 38, 0.96), rgba(8, 17, 30, 0.94));
  border: 1px solid rgba(140, 166, 197, 0.18);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.2);
`;

const HeroPanel = styled(InfoCard)`
  padding: 28px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  margin-bottom: 16px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(250, 176, 5, 0.12);
  border: 1px solid rgba(250, 176, 5, 0.24);
  color: #ffd27a;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const HeroText = styled.p`
  max-width: 680px;
  margin: 16px 0 28px;
  font-size: 1.05rem;
  line-height: 1.75;
  color: rgba(214, 225, 239, 0.8);
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  padding: 16px 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(140, 166, 197, 0.16);

  span {
    display: block;
    margin-bottom: 8px;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(173, 194, 219, 0.72);
  }

  strong {
    color: #f8fbff;
    font-size: 1rem;
    line-height: 1.4;
  }
`;

const PanelLabel = styled.div`
  margin-bottom: 10px;
  color: #7dd3fc;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const PanelTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 1.45rem;
  color: #f8fbff;
`;

const PanelText = styled.p`
  margin: 0 0 22px;
  line-height: 1.75;
  color: rgba(214, 225, 239, 0.78);
`;

const PanelHighlights = styled.div`
  display: grid;
  gap: 12px;
`;

const HighlightItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(140, 166, 197, 0.14);
  color: rgba(228, 236, 247, 0.82);

  span {
    display: inline-flex;
    min-width: 54px;
    justify-content: center;
    padding: 8px 10px;
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(250, 176, 5, 0.2), rgba(34, 211, 238, 0.18));
    color: #f7fbff;
    font-weight: 900;
    font-size: 0.85rem;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 18px;
`;

const SectionLead = styled.p`
  margin: 8px 0 0;
  color: rgba(196, 213, 233, 0.74);
  line-height: 1.7;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const InfoLabel = styled.div`
  margin-bottom: 10px;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(173, 194, 219, 0.72);
`;

const InfoValue = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
  color: #f7fbff;
  line-height: 1.5;
`;

const WhyCard = styled(InfoCard)`
  margin-bottom: 40px;
  padding: 26px;
`;

const WhyTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 1.35rem;
  color: #f8fbff;
`;

const WhyText = styled.p`
  margin: 0;
  line-height: 1.8;
  color: rgba(213, 226, 241, 0.78);
`;

const CTAHelper = styled.p`
  margin: 14px 0 0;
  color: rgba(191, 208, 229, 0.68);
  font-size: 0.92rem;
`;

const BackgroundOrb = styled.div`
  position: absolute;
  width: 340px;
  height: 340px;
  border-radius: 999px;
  filter: blur(18px);
  pointer-events: none;
  background: ${({ $color }) => $color};
  top: ${({ $top }) => $top || "auto"};
  right: ${({ $right }) => $right || "auto"};
  bottom: ${({ $bottom }) => $bottom || "auto"};
  left: ${({ $left }) => $left || "auto"};
`;

const LoadingShell = styled.div`
  position: relative;
  z-index: 1;
  max-width: 760px;
  margin: 0 auto;
  padding: 42px 32px;
  border-radius: 32px;
  background: linear-gradient(180deg, rgba(10, 22, 38, 0.96), rgba(8, 17, 30, 0.94));
  border: 1px solid rgba(140, 166, 197, 0.18);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.2);
  text-align: center;
`;

const LoadingEyebrow = styled(Eyebrow)`
  margin: 0 auto 16px;
`;

const LoadingTitle = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 4vw, 3rem);
  color: #f8fbff;
`;

const LoadingText = styled.p`
  max-width: 620px;
  margin: 16px auto 24px;
  line-height: 1.8;
  color: rgba(213, 226, 241, 0.78);
`;

const LoadingBar = styled.div`
  height: 12px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(140, 166, 197, 0.12);
`;

const LoadingProgress = styled.div`
  width: 42%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(135deg, #f9b005 0%, #f59e0b 46%, #22d3ee 100%);
  animation: shimmer 1.4s ease-in-out infinite alternate;

  @keyframes shimmer {
    from {
      transform: translateX(-8%);
    }
    to {
      transform: translateX(32%);
    }
  }
`;
